/**
 * Catálogo de permisos por capacidad (`modulo.subcapacidad`) y acciones CRUD.
 * Extensible: agregar ítems al array sin cambiar el modelo en Firestore.
 */

export const PERMISOS_VERSION = 1;

export type PermisoAccion = 'show' | 'add' | 'edit' | 'delete';

export type PermisoCrudFlags = Partial<Record<PermisoAccion, boolean>>;

export type PermisosModulosMap = Record<string, PermisoCrudFlags>;

export interface PermisoCatalogoItem {
  key: string;
  label: string;
  descripcion?: string;
  grupo: string;
  orden: number;
  legacyRole?: string;
  /** Si el tenant no expone la feature, no se ofrece en el modal. */
  requiereFeature?: string;
  /** Acciones que tiene sentido mostrar (p. ej. listados sin «agregar»). */
  accionesDisponibles?: readonly PermisoAccion[];
}

export const PERMISO_ACCIONES: readonly PermisoAccion[] = ['show', 'add', 'edit', 'delete'];

export const ETIQUETA_PERMISO_ACCION: Record<PermisoAccion, string> = {
  show: 'Ver',
  add: 'Agregar',
  edit: 'Editar',
  delete: 'Eliminar / baja',
};

export const ETIQUETA_PERMISO_GRUPO: Record<string, string> = {
  socios: 'Socios',
  servicios: 'Servicios',
  liquidacion: 'Liquidación',
  listados: 'Listados',
  admin: 'Administración',
  auditoria: 'Auditoría',
  contable: 'Contabilidad',
  cooperativaEspecial: 'Cooperativa especial',
};

/** Catálogo v1 — permisos por módulo de la aplicación. */
export const PERMISOS_CATALOGO: readonly PermisoCatalogoItem[] = [
  { key: 'socios.buscar', label: 'Buscar socios', grupo: 'socios', orden: 10, legacyRole: 'ROLE_SOCIOS_BUSCAR' },
  {
    key: 'socios.datosGenerales',
    label: 'Datos generales del socio',
    descripcion: 'Nombre, documento, domicilio en ficha, alta de socio.',
    grupo: 'socios',
    orden: 20,
    legacyRole: 'ROLE_SOCIOS_DATOS',
  },
  {
    key: 'socios.datosBancarios',
    label: 'Datos bancarios del socio',
    descripcion: 'CBU, banco, cuenta para acreditación en esta cooperativa.',
    grupo: 'socios',
    orden: 30,
    legacyRole: 'ROLE_SOCIOS_BANCOS',
  },
  { key: 'socios.contactos', label: 'Contactos del socio', grupo: 'socios', orden: 40, legacyRole: 'ROLE_SOCIOS_CONTACTOS' },
  { key: 'socios.familiares', label: 'Familiares', grupo: 'socios', orden: 50, legacyRole: 'ROLE_SOCIOS_FAMILIARES' },
  { key: 'socios.obraSocial', label: 'Obra social / servicio de salud', grupo: 'socios', orden: 60, legacyRole: 'ROLE_SOCIOS_OBRA_SOCIAL' },
  {
    key: 'socios.datosImpositivos',
    label: 'Datos impositivos',
    grupo: 'socios',
    orden: 70,
    legacyRole: 'ROLE_SOCIOS_DATOS_IMPOSITIVOS',
  },
  { key: 'socios.conceptos', label: 'Conceptos del socio', grupo: 'socios', orden: 80, legacyRole: 'ROLE_SOCIOS_CONCEPTOS' },
  { key: 'socios.inasistencias', label: 'Inasistencias', grupo: 'socios', orden: 90, legacyRole: 'ROLE_SOCIOS_INASISTENCIAS' },
  { key: 'socios.legajo', label: 'Legajo', grupo: 'socios', orden: 100, legacyRole: 'ROLE_SOCIOS_LEGAJO' },
  {
    key: 'socios.legajo.borrar',
    label: 'Borrar archivos del legajo',
    grupo: 'socios',
    orden: 105,
    legacyRole: 'ROLE_SOCIOS_LEGAJO_BORRAR',
    accionesDisponibles: ['show', 'delete'],
  },
  { key: 'socios.vacaciones', label: 'Vacaciones', grupo: 'socios', orden: 110, legacyRole: 'ROLE_SOCIOS_VACACIONES' },
  { key: 'socios.estadoCuenta', label: 'Estado de cuenta', grupo: 'socios', orden: 120, legacyRole: 'ROLE_SOCIOS_ESTADO_CUENTA' },
  { key: 'socios.uniformes', label: 'Uniformes', grupo: 'socios', orden: 130, legacyRole: 'ROLE_SOCIOS_UNIFORMES' },
  { key: 'socios.actividades', label: 'Actividades', grupo: 'socios', orden: 140, legacyRole: 'ROLE_SOCIOS_ACTIVIDADES' },
  {
    key: 'socios.adelantoOrdinario',
    label: 'Adelanto',
    grupo: 'socios',
    orden: 150,
    legacyRole: 'ROLE_SOCIOS_ADEL_ORDINARIOS',
  },
  { key: 'socios.altaActa', label: 'Alta por acta', grupo: 'socios', orden: 160, legacyRole: 'ROLE_SOCIOS_ALTA_ACTA' },
  { key: 'socios.bajaActa', label: 'Baja por acta', grupo: 'socios', orden: 170, legacyRole: 'ROLE_SOCIOS_BAJA_ACTA' },
  {
    key: 'socios.acta.quitarSocio',
    label: 'Quitar socio de un acta',
    descripcion: 'Sacar a un socio que ya estaba grabado en un acta (queda en auditoría).',
    grupo: 'socios',
    orden: 175,
    accionesDisponibles: ['show', 'delete'],
  },
  { key: 'socios.vinculacion', label: 'Alta/baja monotributo', grupo: 'socios', orden: 180, legacyRole: 'ROLE_SOCIOS_VINCULACION' },
  { key: 'socios.tramiteArba', label: 'Trámite ARBA', grupo: 'socios', orden: 190, legacyRole: 'ROLE_SOCIOS_TRAMITE_ARBA' },
  {
    key: 'servicios.carga',
    label: 'Carga de servicios',
    descripcion: 'Carga masiva y listado de servicios cargados (movimientos / préstamos).',
    grupo: 'servicios',
    orden: 195,
  },
  {
    key: 'servicios.faltantes',
    label: 'Servicio faltantes',
    grupo: 'servicios',
    orden: 200,
    legacyRole: 'ROLE_SERVICIOS_FALTANTES',
  },
  {
    key: 'servicios.pendientes',
    label: 'Servicios pendientes',
    grupo: 'servicios',
    orden: 210,
    legacyRole: 'ROLE_SERVICIOS_PENDIENTES',
  },
  {
    key: 'liquidacion.liquidar',
    label: 'Liquidar',
    grupo: 'liquidacion',
    orden: 300,
    legacyRole: 'ROLE_LIQ_LIQUIDACION',
  },
  {
    key: 'liquidacion.exportacion',
    label: 'Exportación liquidación',
    grupo: 'liquidacion',
    orden: 310,
    legacyRole: 'ROLE_LIQ_EXPORTACION',
  },
  {
    key: 'liquidacion.comprobantes',
    label: 'Comprobantes liquidación',
    grupo: 'liquidacion',
    orden: 320,
    legacyRole: 'ROLE_LIQ_COMPROBANTES',
  },
  {
    key: 'listados.generalSocios',
    label: 'Listado general de socios',
    grupo: 'listados',
    orden: 400,
    legacyRole: 'ROLE_LISTADO_GENERAL_SOCIOS',
    accionesDisponibles: ['show', 'edit', 'delete'],
  },
  {
    key: 'listados.exportTxtBanco',
    label: 'Exportar TXT bancos',
    grupo: 'listados',
    orden: 410,
    legacyRole: 'ROLE_EXPORT_TXT',
    accionesDisponibles: ['show', 'edit'],
  },
  {
    key: 'admin.usuarios',
    label: 'Usuarios de la cooperativa',
    grupo: 'admin',
    orden: 500,
    legacyRole: 'ROLE_ADMIN_USUARIOS',
  },
  {
    key: 'admin.datosCooperativa',
    label: 'Datos, logo y firma de la cooperativa',
    grupo: 'admin',
    orden: 510,
    legacyRole: 'ROLE_ADMIN_DATOS_SISTEMA',
  },
  {
    key: 'admin.almacenamiento',
    label: 'Integración de almacenamiento',
    grupo: 'admin',
    orden: 520,
  },
  {
    key: 'admin.sitioWeb',
    label: 'Sitio web institucional',
    descripcion: 'Contenido del sitio público (carpeta sitio en Drive, enlaces y ayuda).',
    grupo: 'admin',
    orden: 525,
  },
  {
    key: 'admin.monotributo',
    label: 'Catálogo monotributo ARCA',
    grupo: 'admin',
    orden: 530,
    legacyRole: 'ROLE_ADMIN_MONOTRIBUTO',
  },
  {
    key: 'admin.clientesLugares',
    label: 'Catálogo clientes y lugares',
    grupo: 'admin',
    orden: 540,
    legacyRole: 'ROLE_ADMIN_CLIENTES',
  },
  {
    key: 'admin.regiones',
    label: 'Catálogo de regiones',
    grupo: 'admin',
    orden: 545,
  },
  {
    key: 'admin.servicios',
    label: 'Catálogo de servicios',
    grupo: 'admin',
    orden: 550,
    legacyRole: 'ROLE_ADMIN_SERVICIOS',
  },
  {
    key: 'admin.servicioSalud',
    label: 'Catálogo servicios de salud',
    grupo: 'admin',
    orden: 560,
    legacyRole: 'ROLE_ADMIN_OBRA_SOCIAL',
  },
  {
    key: 'admin.conceptosLiquidacion',
    label: 'Conceptos de liquidación',
    grupo: 'admin',
    orden: 570,
    legacyRole: 'ROLE_ADMIN_CONCEPTOS',
  },
  {
    key: 'admin.periodos',
    label: 'Períodos (mes operativo)',
    descripcion: 'Ver y abrir el período mensual abierto (liquidación / operación).',
    grupo: 'admin',
    orden: 575,
  },
  {
    key: 'auditoria.consulta',
    label: 'Consulta de auditoría',
    descripcion: 'Actividad, cambios de datos y accesos registrados.',
    grupo: 'auditoria',
    orden: 600,
    legacyRole: 'ROLE_AUDITORIA',
    accionesDisponibles: ['show'],
  },
  {
    key: 'contable.operaciones',
    label: 'Módulo contable',
    grupo: 'contable',
    orden: 700,
    requiereFeature: 'moduloContable',
  },
  {
    key: 'cooperativaEspecial.gestion',
    label: 'Cooperativa especial',
    grupo: 'cooperativaEspecial',
    orden: 800,
    requiereFeature: 'cooperativaEspecial',
  },
];

const CATALOGO_POR_KEY = new Map(PERMISOS_CATALOGO.map((c) => [c.key, c]));

export function catalogoPermisoPorKey(key: string): PermisoCatalogoItem | undefined {
  return CATALOGO_POR_KEY.get(key);
}

export function accionesParaCatalogoItem(item: PermisoCatalogoItem): readonly PermisoAccion[] {
  return item.accionesDisponibles ?? PERMISO_ACCIONES;
}

/** Normaliza flags: add/edit/delete implican show. */
export function normalizarPermisoCrudFlags(raw: PermisoCrudFlags | undefined): PermisoCrudFlags {
  const f: PermisoCrudFlags = {
    show: !!raw?.show,
    add: !!raw?.add,
    edit: !!raw?.edit,
    delete: !!raw?.delete,
  };
  if (f.add || f.edit || f.delete) {
    f.show = true;
  }
  return f;
}

export function normalizarPermisosModulosMap(raw: PermisosModulosMap | undefined | null): PermisosModulosMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: PermisosModulosMap = {};
  for (const [key, flags] of Object.entries(raw)) {
    if (!key.trim() || !flags || typeof flags !== 'object') continue;
    out[key] = normalizarPermisoCrudFlags(flags as PermisoCrudFlags);
  }
  return out;
}

export function flagsVacios(): PermisoCrudFlags {
  return { show: false, add: false, edit: false, delete: false };
}

export function catalogoDisponible(features?: Record<string, boolean> | null): PermisoCatalogoItem[] {
  return PERMISOS_CATALOGO.filter((c) => {
    if (!c.requiereFeature) return true;
    return features?.[c.requiereFeature] === true;
  });
}

export function catalogoAgrupado(features?: Record<string, boolean> | null): { grupo: string; label: string; items: PermisoCatalogoItem[] }[] {
  const items = catalogoDisponible(features);
  const byGrupo = new Map<string, PermisoCatalogoItem[]>();
  for (const c of items) {
    const list = byGrupo.get(c.grupo) ?? [];
    list.push(c);
    byGrupo.set(c.grupo, list);
  }
  return [...byGrupo.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'es'))
    .map(([grupo, list]) => ({
      grupo,
      label: ETIQUETA_PERMISO_GRUPO[grupo] ?? grupo,
      items: list.sort((x, y) => x.orden - y.orden),
    }));
}

/** Plantilla inicial al invitar según rol (editable después sin restricción de perfil). */
export function permisosModulosInicialesSegunRol(role: string): PermisosModulosMap {
  const r = role.trim().toLowerCase();
  const all = (keys: string[], level: 'show' | 'edit' | 'full'): PermisosModulosMap => {
    const out: PermisosModulosMap = {};
    for (const key of keys) {
      if (level === 'show') out[key] = { show: true };
      else if (level === 'edit') out[key] = { show: true, add: true, edit: true, delete: false };
      else out[key] = { show: true, add: true, edit: true, delete: true };
    }
    return out;
  };

  const sociosOperativo = [
    'socios.buscar',
    'socios.datosGenerales',
    'socios.contactos',
    'socios.datosBancarios',
    'socios.familiares',
    'socios.obraSocial',
    'socios.datosImpositivos',
    'socios.inasistencias',
    'socios.tramiteArba',
  ];
  const adminKeys = [
    'admin.usuarios',
    'admin.datosCooperativa',
    'admin.almacenamiento',
    'admin.sitioWeb',
    'admin.monotributo',
    'admin.clientesLugares',
    'admin.regiones',
    'admin.servicios',
    'admin.servicioSalud',
    'admin.conceptosLiquidacion',
    'admin.periodos',
  ];

  if (r === 'administrador') {
    return {
      ...all(sociosOperativo, 'full'),
      ...all(
        [
          'socios.conceptos',
          'socios.legajo',
          'socios.legajo.borrar',
          'socios.vacaciones',
          'socios.estadoCuenta',
          'socios.uniformes',
          'socios.actividades',
          'socios.adelantoOrdinario',
          'socios.altaActa',
          'socios.bajaActa',
          'socios.acta.quitarSocio',
          'socios.vinculacion',
          'listados.generalSocios',
          'listados.exportTxtBanco',
          'servicios.carga',
          'servicios.faltantes',
          'servicios.pendientes',
          'liquidacion.liquidar',
          'liquidacion.exportacion',
          'liquidacion.comprobantes',
          'auditoria.consulta',
        ],
        'full'
      ),
      ...all(adminKeys, 'full'),
    };
  }

  if (r === 'administrativo') {
    return {
      ...all(sociosOperativo, 'edit'),
      ...all(['listados.generalSocios', 'servicios.carga', 'servicios.pendientes'], 'edit'),
      ...all(adminKeys.filter((k) => k !== 'admin.usuarios'), 'edit'),
      'admin.usuarios': { show: true, edit: false },
    };
  }

  if (r === 'supervisor') {
    return {
      ...all(['socios.buscar', 'socios.datosGenerales', 'socios.contactos', 'socios.inasistencias'], 'edit'),
      'socios.datosBancarios': { show: true },
    };
  }

  return all(['socios.buscar'], 'show');
}

const MODULO_A_PERMISO_BASICO: Record<string, string> = {
  'admin.usuarios': 'administracionUsuarios',
  'admin.datosCooperativa': 'datosCooperativa',
  'admin.almacenamiento': 'integracionAlmacenamiento',
  'admin.sitioWeb': 'sitioWebCatalogo',
  'admin.monotributo': 'catalogoMonotributo',
  'admin.clientesLugares': 'clientesLugaresCatalogo',
  'admin.regiones': 'regionesCatalogo',
  'admin.servicios': 'serviciosCatalogo',
  'admin.servicioSalud': 'servicioSaludCatalogo',
  'admin.conceptosLiquidacion': 'liquidacionConceptosCatalogo',
  'admin.periodos': 'periodosGestion',
};

/** Sincroniza booleanos legacy para reglas/callables existentes. */
export function permisosBasicosDesdeModulos(modulos: PermisosModulosMap): Record<string, boolean> {
  const tieneSocioShow = Object.entries(modulos).some(
    ([k, f]) => k.startsWith('socios.') && !!f?.show
  );

  const out: Record<string, boolean> = {
    workspace: true,
    socios: tieneSocioShow,
    administracionUsuarios: false,
    datosCooperativa: false,
    integracionAlmacenamiento: false,
    catalogoMonotributo: false,
    clientesLugaresCatalogo: false,
    regionesCatalogo: false,
    serviciosCatalogo: false,
    servicioSaludCatalogo: false,
    liquidacionConceptosCatalogo: false,
    periodosGestion: false,
    sitioWebCatalogo: false,
  };

  for (const [modKey, basico] of Object.entries(MODULO_A_PERMISO_BASICO)) {
    const f = modulos[modKey];
    if (f?.edit || f?.add || f?.delete) {
      out[basico] = true;
    }
    if (basico === 'administracionUsuarios' && f?.show) {
      out[basico] = out[basico] || !!f?.edit;
    }
  }

  if (modulos['admin.usuarios']?.edit) {
    out['administracionUsuarios'] = true;
  }

  return out;
}

export interface PermisoEvalContext {
  isRoot?: boolean;
  role?: string | null;
  permisos?: Record<string, boolean> | null;
  permisosModulos?: PermisosModulosMap | null;
}

/** Hay al menos una clave asignada en `permisosModulos` (modo estricto: solo lo listado cuenta). */
export function tienePermisosModulosAsignados(ctx: PermisoEvalContext): boolean {
  return Object.keys(normalizarPermisosModulosMap(ctx.permisosModulos ?? undefined)).length > 0;
}

export function tienePermisoModulo(
  ctx: PermisoEvalContext,
  key: string,
  accion: PermisoAccion
): boolean {
  if (ctx.isRoot) return true;

  const modulos = normalizarPermisosModulosMap(ctx.permisosModulos ?? undefined);
  const estricto = Object.keys(modulos).length > 0;

  if (estricto) {
    const flags = modulos[key];
    if (!flags) return false;
    return !!normalizarPermisoCrudFlags(flags)[accion];
  }

  const flags = modulos[key];
  if (flags) {
    return !!normalizarPermisoCrudFlags(flags)[accion];
  }

  const rol = (ctx.role ?? '').trim().toLowerCase();
  if (rol === 'administrador' || rol === 'administrativo') {
    if (key.startsWith('admin.') && accion !== 'delete') return true;
    if (key.startsWith('socios.') && accion === 'show') return true;
  }
  if (rol === 'supervisor' && key.startsWith('socios.') && accion === 'show') {
    return true;
  }

  const basico = MODULO_A_PERMISO_BASICO[key];
  if (basico && ctx.permisos?.[basico] === true) {
    return accion === 'show' || accion === 'edit';
  }

  if (key.startsWith('socios.') && ctx.permisos?.['socios'] === true) {
    return accion === 'show' || accion === 'edit';
  }

  return false;
}

/** Alguna capacidad bajo `socios.`, `admin.`, etc. con la acción indicada. */
export function tieneAlgunPermisoConPrefijo(
  ctx: PermisoEvalContext,
  prefijo: string,
  accion: PermisoAccion
): boolean {
  if (ctx.isRoot) return true;
  const modulos = normalizarPermisosModulosMap(ctx.permisosModulos ?? undefined);
  if (Object.keys(modulos).length > 0) {
    return Object.entries(modulos).some(
      ([k, f]) => k.startsWith(prefijo) && !!normalizarPermisoCrudFlags(f)[accion]
    );
  }
  if (prefijo === 'socios.' && ctx.permisos?.['socios'] === true) {
    return accion === 'show' || accion === 'edit';
  }
  return false;
}

/** Mapa sección ficha socio → clave de permiso. */
export const SECCION_FICHA_A_PERMISO: Record<string, string> = {
  personales: 'socios.datosGenerales',
  domicilio: 'socios.datosGenerales',
  contactos: 'socios.contactos',
  bancarios: 'socios.datosBancarios',
  servicioSalud: 'socios.obraSocial',
  familiares: 'socios.familiares',
  impositivo: 'socios.datosImpositivos',
  tramiteArba: 'socios.tramiteArba',
  inasistencias: 'socios.inasistencias',
  legajo: 'socios.legajo',
  vacaciones: 'socios.vacaciones',
  uniformes: 'socios.uniformes',
  estadoCuenta: 'socios.estadoCuenta',
  actividades: 'socios.actividades',
  adelantoOrdinario: 'socios.adelantoOrdinario',
  conceptosSocio: 'socios.conceptos',
  sanciones: 'socios.datosGenerales',
};

export function resumenPermisosModulosAsignados(modulos: PermisosModulosMap): string[] {
  return Object.keys(normalizarPermisosModulosMap(modulos))
    .map((key) => {
      const item = catalogoPermisoPorKey(key);
      const f = modulos[key]!;
      const acts = PERMISO_ACCIONES.filter((a) => f[a]).map((a) => ETIQUETA_PERMISO_ACCION[a].toLowerCase());
      return `${item?.label ?? key}: ${acts.join(', ') || '—'}`;
    })
    .sort((a, b) => a.localeCompare(b, 'es'));
}

/** Claves válidas del catálogo (para tests / validación). */
export const PERMISOS_CATALOGO_KEYS = PERMISOS_CATALOGO.map((c) => c.key);
